import { useState } from "react";

import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import type { Subscription } from "../lib/types";

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "");

/**
 * When a paid plan renews or ends, with Cancel / Resume right there (ADR-0033).
 * Cancelling keeps the plan until the end of the period already paid for.
 */
export function SubscriptionControls({ sub, name, onChange }: { sub: Subscription | null | undefined; name: string; onChange: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!sub || !["active", "trialing", "past_due"].includes(sub.status) || sub.price === 0) {
    return sub?.provider === "grant" && ["active", "trialing"].includes(sub.status)
      ? <span className="small muted">Given to you by an administrator{sub.current_period_end ? ` until ${date(sub.current_period_end)}` : ""}.</span>
      : null;
  }
  const act = async (action: "cancel" | "resume") => {
    if (action === "cancel" && !confirm(`Cancel ${name}? You keep it until ${date(sub.current_period_end) || "the end of the period you paid for"}, and you won't be charged again.`)) return;
    setBusy(true);
    try {
      await api(`/api/billing/subscriptions/${sub.id}/${action}`, { method: "POST" });
      toast(action === "cancel" ? `${name} is cancelled; it runs until ${date(sub.current_period_end)}` : `${name} will renew`, "success");
      onChange();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  const when = date(sub.current_period_end);
  return (
    <div className="row between" style={{ gap: 12, flexWrap: "wrap" }}>
      <span className="small muted">
        {sub.cancel_at_period_end
          ? `Cancelled: ends ${when}`
          : sub.status === "trialing"
            ? `Free trial until ${when}, then it renews`
            : sub.status === "past_due"
              ? "Payment failed: update your card in the email from Stripe"
              : when ? `Renews ${when}` : "Renews automatically"}
      </span>
      {sub.cancel_at_period_end
        ? <button className="btn btn-sm" disabled={busy} onClick={() => act("resume")}>Keep {name}</button>
        : <button className="btn btn-sm btn-ghost danger" disabled={busy} onClick={() => act("cancel")}>Cancel subscription</button>}
    </div>
  );
}
